/**
 * Arte de las cartas.
 *
 * Cada carta tiene su hueco de ilustración ya montado, con un PNG provisional
 * generado en `src/assets/cards/`. Sustituir ese fichero por la ilustración
 * definitiva —mismo nombre— es todo lo que hace falta: ni código, ni catálogo,
 * ni base de datos.
 *
 * El icono de Lucide sigue existiendo como último recurso, para una carta que
 * todavía no tenga ni imagen provisional.
 */

import {
  Bomb,
  Flame,
  FlaskConical,
  Ghost,
  Hammer,
  HelpCircle,
  Mountain,
  Music,
  PawPrint,
  Shield,
  Sparkles,
  Sword,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import { CARD_ART } from '@/assets/cards';
import type { CardMaster, Rarity } from '@/types/game';

const ICONS: Record<string, LucideIcon> = {
  Bomb,
  Flame,
  FlaskConical,
  Ghost,
  Hammer,
  Mountain,
  Music,
  PawPrint,
  Shield,
  Sparkles,
  Sword,
  Wind,
};

/** Icono de una carta. Cae en un interrogante si el catálogo pide uno que no existe. */
export function iconFor(name: string): LucideIcon {
  return ICONS[name] ?? HelpCircle;
}

/**
 * Ilustración de una carta, o null si hay que caer en el icono.
 *
 * Orden de preferencia:
 *   1. `imageUrl` del catálogo — arte servido desde fuera (Supabase Storage o un
 *      CDN). Es como llegará el arte de las cartas que se publiquen después del
 *      lanzamiento, sin necesidad de desplegar la aplicación otra vez.
 *   2. El PNG incluido en el paquete. Vite le pone un hash al empaquetar, así
 *      que se puede cachear para siempre y aun así actualizarse al cambiarlo.
 */
export function artUrlFor(master: CardMaster): string | null {
  return master.imageUrl ?? CARD_ART[master.id] ?? null;
}

export interface RarityStyle {
  /** Borde de la carta. */
  border: string;
  /** Halo exterior: es lo que hace que una legendaria destaque de un vistazo. */
  glow: string;
  /** Color del texto del nombre. */
  text: string;
  label: string;
}

export const RARITY_STYLES: Record<Rarity, RarityStyle> = {
  COMMON: {
    border: 'border-slate-400/60',
    glow: '',
    text: 'text-slate-100',
    label: 'Común',
  },
  RARE: {
    border: 'border-sky-400/80',
    glow: 'shadow-[0_0_12px_-2px_rgba(56,189,248,0.7)]',
    text: 'text-sky-100',
    label: 'Rara',
  },
  EPIC: {
    border: 'border-fuchsia-400/80',
    glow: 'shadow-[0_0_14px_-2px_rgba(232,121,249,0.75)]',
    text: 'text-fuchsia-100',
    label: 'Épica',
  },
  LEGENDARY: {
    border: 'border-amber-300',
    glow: 'shadow-[0_0_18px_-1px_rgba(252,211,77,0.85)]',
    text: 'text-amber-100',
    label: 'Legendaria',
  },
};
